import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';

const SchedulingFeatureContext = createContext(undefined);

// Tier that includes scheduling
const SCHEDULING_TIER = 'on_ya_royalties';
const SCHEDULING_TIER_NAME = 'Own Ya Royalties';

export const SchedulingFeatureProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [userSubscription, setUserSubscription] = useState(null);
  const [hasSchedulingAccess, setHasSchedulingAccess] = useState(false);
  const [hasScheduledBefore, setHasScheduledBefore] = useState(false);
  const [discoveryDismissed, setDiscoveryDismissed] = useState(false);
  const [adminSettings, setAdminSettings] = useState({
    schedulingAnnouncementsEnabled: true,
    announcementAudience: 'all' // 'all', 'new_users', 'creators'
  });
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Check if user should see announcements based on admin settings
  const shouldShowAnnouncements = useCallback(() => {
    if (!adminSettings.schedulingAnnouncementsEnabled) return false;
    if (!user) return false;
    
    const audience = adminSettings.announcementAudience;
    if (audience === 'all') return true;
    
    if (audience === 'new_users') {
      // Check if user is new (≤14 days)
      const createdAt = new Date(user.created_at);
      const daysSinceCreation = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24);
      return daysSinceCreation <= 14;
    }
    
    if (audience === 'creators') {
      // Check if user has creator role or has posted content
      return user.role === 'creator' || user.content_count > 0;
    }
    
    return true;
  }, [adminSettings, user]);

  // Check subscription and scheduling access
  useEffect(() => {
    const checkAccess = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Fetch subscription
        const subResponse = await fetch(`${API_URL}/api/subscriptions/my-subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (subResponse.ok) {
          const subData = await subResponse.json();
          setUserSubscription(subData);
          
          // Check if tier includes scheduling
          const planId = subData?.plan_id || subData?.plan?.id || 'basic';
          setHasSchedulingAccess(planId === SCHEDULING_TIER || planId === 'on_ya_royalties');
        }

        // Check if user has scheduled before (for discovery UI dismissal)
        const scheduledResponse = await fetch(`${API_URL}/api/content/scheduled`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (scheduledResponse.ok) {
          const scheduledData = await scheduledResponse.json();
          const hasScheduled = (scheduledData.scheduled_posts?.length || 0) > 0;
          setHasScheduledBefore(hasScheduled);
        }

        // Check localStorage for discovery dismissal
        const dismissed = localStorage.getItem('scheduling-discovery-dismissed');
        setDiscoveryDismissed(dismissed === 'true');

      } catch (error) {
        console.error('Error checking scheduling access:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [token, API_URL]);

  // Dismiss discovery UI
  const dismissDiscovery = useCallback(() => {
    setDiscoveryDismissed(true);
    localStorage.setItem('scheduling-discovery-dismissed', 'true');
    trackAnalytics('scheduling_discovery_dismissed');
  }, []);

  // Track analytics
  const trackAnalytics = useCallback(async (event, metadata = {}) => {
    try {
      await fetch(`${API_URL}/api/analytics/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          event,
          category: 'scheduling_feature',
          metadata: {
            ...metadata,
            user_tier: userSubscription?.plan_id || 'basic',
            has_access: hasSchedulingAccess,
            timestamp: new Date().toISOString()
          }
        })
      });
    } catch (error) {
      // Silent fail for analytics
      console.debug('Analytics tracking failed:', error);
    }
  }, [API_URL, token, userSubscription, hasSchedulingAccess]);

  // Track specific events
  const trackUpgradeViewed = useCallback((placement) => {
    trackAnalytics('scheduling_upgrade_viewed', { placement });
  }, [trackAnalytics]);

  const trackUpgradeClicked = useCallback((placement) => {
    trackAnalytics('scheduling_upgrade_clicked', { placement });
  }, [trackAnalytics]);

  const trackUpgradeStarted = useCallback(() => {
    trackAnalytics('scheduling_upgrade_started');
  }, [trackAnalytics]);

  const trackUpgradeCompleted = useCallback(() => {
    trackAnalytics('scheduling_upgrade_completed');
  }, [trackAnalytics]);

  const trackUnlockShown = useCallback(() => {
    trackAnalytics('scheduling_unlock_shown');
  }, [trackAnalytics]);

  // Show discovery UI only if user has access, hasn't scheduled before, and hasn't dismissed
  const shouldShowDiscovery = hasSchedulingAccess && !hasScheduledBefore && !discoveryDismissed && shouldShowAnnouncements();

  // Show upgrade prompt only if user doesn't have access
  const shouldShowUpgrade = !hasSchedulingAccess && shouldShowAnnouncements();

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    // Access states
    hasSchedulingAccess,
    hasScheduledBefore,
    userSubscription,
    loading,
    
    // UI states
    shouldShowDiscovery,
    shouldShowUpgrade,
    dismissDiscovery,
    
    // Constants
    SCHEDULING_TIER,
    SCHEDULING_TIER_NAME,
    
    // Admin settings
    adminSettings,
    setAdminSettings,
    
    // Analytics
    trackUpgradeViewed,
    trackUpgradeClicked,
    trackUpgradeStarted,
    trackUpgradeCompleted,
    trackUnlockShown,
    trackAnalytics
  }), [
    hasSchedulingAccess,
    hasScheduledBefore,
    userSubscription,
    loading,
    shouldShowDiscovery,
    shouldShowUpgrade,
    dismissDiscovery,
    adminSettings,
    setAdminSettings,
    trackUpgradeViewed,
    trackUpgradeClicked,
    trackUpgradeStarted,
    trackUpgradeCompleted,
    trackUnlockShown,
    trackAnalytics
  ]);

  return (
    <SchedulingFeatureContext.Provider value={contextValue}>
      {children}
    </SchedulingFeatureContext.Provider>
  );
};

export const useSchedulingFeature = () => {
  const context = useContext(SchedulingFeatureContext);
  if (context === undefined) {
    throw new Error('useSchedulingFeature must be used within a SchedulingFeatureProvider');
  }
  return context;
};

export default SchedulingFeatureContext;
